import { useEffect, useState } from "react";
import { Phone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const STATUSES = ["new", "contacted", "in-progress", "closed"];
const STATUS_STYLE = {
  new: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  contacted: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  "in-progress": "bg-sky-500/15 text-sky-400 border-sky-500/30",
  closed: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

export default function LeadsTab() {
  const [leads, setLeads] = useState([]);

  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success("Lead updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const fmt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

  return (
    <div className="mt-6" data-testid="leads-tab">
      <h2 className="font-display text-2xl font-bold text-white mb-5">Leads ({leads.length})</h2>
      {leads.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="leads-empty">No leads yet. New submissions from the site appear here instantly.</p>
      ) : (
        <div className="space-y-3">
          {leads.map((l) => (
            <div key={l.id} data-testid={`lead-row-${l.id}`}
              className="bg-[#131C38] border border-[#1E293B] rounded-lg p-5 flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-semibold text-white text-sm" data-testid="lead-name">{l.name}</span>
                  <a href={`tel:${l.phone.replace(/[^+\d]/g, "")}`} data-testid="lead-phone"
                    className="flex items-center gap-1 text-xs text-[#F97316] hover:underline">
                    <Phone className="w-3 h-3" /> {l.phone}
                  </a>
                  {l.company && <span className="text-xs text-slate-400">{l.company}</span>}
                </div>
                {l.note && <p className="text-xs text-slate-400 mt-2 leading-relaxed" data-testid="lead-note">{l.note}</p>}
                <p className="font-mono2 text-[10px] text-slate-500 mt-2" data-testid="lead-timestamp">{fmt(l.created_at)}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)}
                  data-testid={`lead-status-${l.id}`}
                  className={`text-[11px] font-mono2 uppercase tracking-wider border rounded-md px-2.5 py-1.5 bg-transparent focus:outline-none ${STATUS_STYLE[l.status] || STATUS_STYLE.new}`}>
                  {STATUSES.map((s) => <option key={s} value={s} className="bg-[#131C38] text-slate-200">{s}</option>)}
                </select>
                <button onClick={() => remove(l.id)} data-testid={`lead-delete-${l.id}`}
                  className="p-2 text-slate-500 hover:text-red-400 transition-colors" aria-label="Delete lead">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
